import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { SITE, VPNS } from "../data/vpns";

const Section = ({ title, children }) => (
  <Reveal className="mb-12">
    <h2 className="mb-4 font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">{title}</h2>
    <div className="article-body">{children}</div>
  </Reveal>
);

const LegalHero = ({ lines, sub }) => (
  <section className="grain relative overflow-hidden bg-[#0F172A]">
    <div className="hero-grid absolute inset-0" />
    <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">Legal</p>
      <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl">
        <MaskedLines lines={lines} />
      </h1>
      {sub && <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{sub}</p>}
    </div>
  </section>
);

export const Terms = () => (
  <Layout>
    <Seo
      title="Terms & Conditions | VPNReviews.ai"
      description="The terms and conditions governing your use of VPNReviews.ai, including permitted use, intellectual property, and limitations of liability."
    />
    <LegalHero lines={["Terms & Conditions"]} sub="Last updated: January 2026" />
    <Breadcrumbs crumbs={[{ label: "Terms & Conditions" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8" data-testid="terms-body">
      <Section title="1. Acceptance of Terms">
        <p>
          By accessing or using vpnreviews.ai (the "Site"), you agree to be bound by these Terms & Conditions. If you
          do not agree, please do not use the Site. We may update these terms from time to time; continued use of the
          Site constitutes acceptance of the updated terms.
        </p>
      </Section>
      <Section title="2. Nature of the Service">
        <p>
          VPNReviews.ai is an independent editorial publication providing reviews, comparisons, rankings, and
          educational content about consumer VPN services. We do not sell VPN services ourselves. All purchases are
          made directly with the respective third-party providers under their own terms.
        </p>
      </Section>
      <Section title="3. Editorial Content & Opinions">
        <p>
          Scores, rankings, and verdicts published on the Site reflect our editorial evaluation based on our own
          testing and assessment at the time of publication. They are comparative opinions, not scientific
          measurements or guarantees of performance. Pricing and features are checked at publication and may change —
          always verify current details with the provider before purchasing.
        </p>
      </Section>
      <Section title="4. Affiliate Relationships">
        <p>
          The Site contains affiliate links. If you purchase through them, we may earn a commission at no additional
          cost to you. See our Affiliate Disclosure and Commercial Relationships pages for full details.
        </p>
      </Section>
      <Section title="5. Intellectual Property">
        <p>
          All original content on the Site — text, layouts, graphics, and test data — is the property of
          VPNReviews.ai and may not be reproduced without permission. Third-party trademarks remain the property of
          their respective owners.
        </p>
      </Section>
      <Section title="6. Permitted Use">
        <ul>
          <li>You may use the Site for personal, non-commercial research purposes</li>
          <li>You may not scrape, republish, or resell Site content without written permission</li>
          <li>You may not use the Site in any way that violates applicable law</li>
        </ul>
      </Section>
      <Section title="7. Limitation of Liability">
        <p>
          The Site is provided "as is" without warranties of any kind. To the maximum extent permitted by law,
          VPNReviews.ai shall not be liable for any damages arising from your use of the Site or from decisions made
          in reliance on our content, including purchases from third-party providers.
        </p>
      </Section>
      <Section title="8. Governing Law & Contact">
        <p>
          Questions about these terms can be sent to {SITE.email}.
        </p>
      </Section>
    </div>
  </Layout>
);

export const Disclaimer = () => (
  <Layout>
    <Seo
      title="Disclaimer of Use | VPNReviews.ai"
      description="Important disclaimers about the informational nature of VPNReviews.ai content, third-party services, pricing accuracy, and external links."
    />
    <LegalHero lines={["Disclaimer of Use"]} sub="Last updated: January 2026" />
    <Breadcrumbs crumbs={[{ label: "Disclaimer of Use" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8" data-testid="disclaimer-body">
      <Section title="Informational Purposes Only">
        <p>
          All content on VPNReviews.ai is provided for general informational and editorial purposes only. It does not
          constitute professional, legal, or security advice. You are responsible for evaluating whether any product
          is suitable for your specific needs and for complying with the laws of your jurisdiction when using a VPN.
        </p>
      </Section>
      <Section title="No Guarantees of Accuracy">
        <p>
          We work hard to keep our reviews, test data, and pricing current, but VPN providers change their services,
          pricing, and features frequently. We make no warranty that all information on the Site is accurate,
          complete, or up to date at the moment you read it. Always verify current pricing and terms directly with
          the provider before purchasing.
        </p>
      </Section>
      <Section title="Third-Party Products & Services">
        <p>
          VPNReviews.ai does not operate, control, or guarantee any of the VPN services we review. Your relationship
          with any provider — including billing, refunds, support, and privacy — is governed solely by that
          provider's own terms and policies. We are not responsible for the performance, availability, or conduct of
          third-party services.
        </p>
      </Section>
      <Section title="External Links">
        <p>
          The Site links to external websites, including affiliate destinations. We are not responsible for the
          content, security, or privacy practices of external sites.
        </p>
      </Section>
      <Section title="Test Results">
        <p>
          Speed, streaming, and latency results reflect our own testing environment at the time of testing. Your
          results will vary based on your location, base connection, device, and network conditions.
        </p>
      </Section>
    </div>
  </Layout>
);

export const EditorialPolicy = () => (
  <Layout>
    <Seo
      title="Editorial Standards & Ranking Policy | VPNReviews.ai"
      description="How VPNReviews.ai tests, scores, and ranks VPN services — and the safeguards that keep affiliate partnerships out of our editorial decisions."
    />
    <LegalHero lines={["Editorial Standards", "& Ranking Policy"]} sub="How our rankings are made — and why they can't be bought." />
    <Breadcrumbs crumbs={[{ label: "Editorial Policy" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8" data-testid="editorial-policy-body">
      <Section title="Our Commitment">
        <p>
          VPNReviews.ai exists to help readers choose the right VPN based on evidence, not marketing. Every ranking,
          score, and verdict on this site is determined by our editorial team through hands-on testing, before and
          independently of any commercial consideration.
        </p>
      </Section>
      <Section title="How We Test">
        <ul>
          <li>Speed: repeated benchmarks across 47 server locations against a fixed base connection</li>
          <li>Streaming: verification against 7 services, including 6 Netflix regional libraries, over 6-week windows</li>
          <li>Security: DNS/IPv6 leak tests, kill switch force-drop tests, and protocol inspection</li>
          <li>Privacy: independent audit records (PwC, Deloitte, KPMG) weighted far above marketing claims</li>
          <li>Usability & value: hands-on app scoring across four platforms and real 2-year pricing</li>
        </ul>
      </Section>
      <Section title="How Scores Are Calculated">
        <p>
          Each VPN receives category scores that are combined into an overall score out of 10. Scores reflect our
          editorial evaluation based on product features, measured performance, pricing, usability, documentation,
          and market reputation. They should be considered comparative opinions rather than scientific measurements.
        </p>
      </Section>
      <Section title="Affiliate Independence Safeguards">
        <ul>
          <li>Rankings are finalised from test data before any commercial arrangement is considered</li>
          <li>All reviewed providers pay broadly similar commission rates, removing any incentive to favour one</li>
          <li>No provider can pay for placement, a higher score, or removal of criticism</li>
          <li>Every review includes cons — including our #1 pick</li>
        </ul>
      </Section>
      <Section title="Corrections & Updates">
        <p>
          We retest monthly and update pricing, features, and scores as products change. If you spot an error, email
          {" "}{SITE.email} and we will review and correct it promptly.
        </p>
      </Section>
    </div>
  </Layout>
);

export const CommercialRelationships = () => (
  <Layout>
    <Seo
      title="Commercial Relationships | VPNReviews.ai"
      description="A transparent list of every commercial and affiliate relationship VPNReviews.ai maintains, and how each is governed."
    />
    <LegalHero lines={["Commercial Relationships"]} sub="Every commercial arrangement we have, listed transparently." />
    <Breadcrumbs crumbs={[{ label: "Commercial Relationships" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8" data-testid="commercial-relationships-body">
      <Section title="How We Make Money">
        <p>
          VPNReviews.ai is funded exclusively through affiliate commissions. When you click a link to a VPN provider
          and make a purchase, the provider may pay us a commission at no additional cost to you. We do not accept
          payment for placement, sponsored rankings, display advertising, or paid reviews.
        </p>
      </Section>
      <Section title="Current Affiliate Partners">
        <ul>
          {VPNS.map((v) => (
            <li key={v.slug}>{v.name} — affiliate program participant</li>
          ))}
        </ul>
        <p>
          All affiliate links on the Site carry rel="nofollow sponsored" attributes in accordance with search engine
          guidelines and FTC disclosure requirements.
        </p>
      </Section>
      <Section title="What Partners Cannot Do">
        <ul>
          <li>Influence scores, rankings, or editorial verdicts</li>
          <li>Preview, approve, or veto reviews before publication</li>
          <li>Pay to remove criticisms or negative findings</li>
        </ul>
      </Section>
      <Section title="Questions">
        <p>
          For questions about any commercial relationship, contact {SITE.email}. See also our Affiliate Disclosure
          and Editorial Standards & Ranking Policy.
        </p>
      </Section>
    </div>
  </Layout>
);
